import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faUpload } from "@fortawesome/free-solid-svg-icons";
import "./ResultsReport.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { classSubjects } from "./subjects.jsx";
import logo from "../Images/aadyacolour.png";



const ResultReport = ({ selectedClass, selectedSection, selectedTest, onBack }) => {
  const [marks, setMarks] = useState({});
  const [maxMarks, setMaxMarks] = useState({});
  const [remarks, setRemarks] = useState({});
  const [traitRemarks, setTraitRemarks] = useState({});
  const [coMarks, setCoMarks] = useState({});
  const [students, setStudents] = useState([]);
  const [currentStudentIndex, setCurrentStudentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [coMarksLoading, setCoMarksLoading] = useState(false);
  const [coMarksError, setCoMarksError] = useState(null);
  const [traitRemarksLoading, setTraitRemarksLoading] = useState(false);
  const [traitRemarksError, setTraitRemarksError] = useState(null);
  const [userRole, setUserRole] = useState("");
  const [isTeacherSubmitted, setIsTeacherSubmitted] = useState(false);
  const [isHeadSubmitted, setIsHeadSubmitted] = useState(false);
  const [isAdminFinalized, setIsAdminFinalized] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [allStudentsData, setAllStudentsData] = useState({});
  const [selectedStream, setSelectedStream] = useState("science");

  const classData = classSubjects[selectedClass] || classSubjects["1"];
  const { scholastic: subjects, coScholastic: coSubjects = [], traitObservations = [], template } = classData;
  const unitTest = `${selectedTest} (2025 – 26)`;

  const determineStream = (marksData) => {
    const scholasticMarks = marksData?.scholastic_marks || {};
    const maxMarks = marksData?.max_marks || {};

    if (Object.keys(scholasticMarks).length === 0 && Object.keys(maxMarks).length === 0) {
      return selectedStream || "science";
    }

    if ("commercial_studies" in scholasticMarks || "commercial_studies" in maxMarks) {
      return "commerce";
    }
    if (
      "physics" in scholasticMarks ||
      "chemistry" in scholasticMarks ||
      "biology" in scholasticMarks ||
      "physics" in maxMarks ||
      "chemistry" in maxMarks ||
      "biology" in maxMarks
    ) {
      return "science";
    }
    return selectedStream || "science";
  };

  const isPrepClass = ["prep 1", "prep 2", "prep 3", "prep i", "prep ii", "prep iii"].includes(
  selectedClass?.toLowerCase().trim()
);

const getTestDisplayName = () => {
  if (isPrepClass) {
    switch (selectedTest) {
      case "I Unit Test":
        return `I Unit Assessment (2025-26)`;
      case "II Unit Test":
        return `II Unit Assessment (2025-26)`;
      case "I Term Examination":
        return `I Term Assessment (2025-26)`;
      case "Annual Examination":
        return `Final Assessment (2026-27)`;   // Only for Prep classes
      default:
        return `${selectedTest} (2025-26)`;
    }
  } else {
    // All other classes (1 to 10) remain unchanged
    return `${selectedTest} (2025-26)`;
  }
};
  const getFilteredSubjects = (stream = selectedStream) => {
    if (!subjects) {
      console.warn(`Subjects are undefined for class ${selectedClass}`);
      return [];
    }

    if (["9", "10"].includes(selectedClass)) {
      if (!subjects || typeof subjects !== "object") {
        console.error(`Invalid subjects structure for class ${selectedClass}:`, subjects);
        return [];
      }

      const commonSubjects = Array.isArray(subjects.common) ? subjects.common : [];
      const streamSubjects = stream === "science"
        ? (Array.isArray(subjects.science) ? subjects.science : [])
        : stream === "commerce"
        ? (Array.isArray(subjects.commerce) ? subjects.commerce : [])
        : [];

      const filtered = [...commonSubjects, ...streamSubjects].filter(
        (subject) => subject && subject.id && subject.name
      );

      const testType = selectedTest.toLowerCase().includes("unit") ? "unitTest" : "termExam";
      return filtered.map(subject => ({
        ...subject,
        maxMarks: subject.maxMarks?.[testType] ?? subject.maxMarks ?? 100,
      }));
    }

    const testType = selectedTest.toLowerCase().includes("unit") ? "unitTest" : "termExam";
    const result = Array.isArray(subjects) ? subjects.map(subject => ({
      ...subject,
      maxMarks: subject.maxMarks?.[testType] ?? subject.maxMarks ?? 100,
    })) : [];
    console.log(`[Class ${selectedClass}] filteredSubjects:`, result);
    return result;
  };

  const filteredSubjects = useMemo(() => getFilteredSubjects(), [selectedClass, selectedStream, subjects]);

  const API_URL = "https://aadyaacademy.org/api";

  const getRequiredHead = (className) => {
    className = className?.toLowerCase().trim() || "";
    if (["prep 1", "prep 2", "prep 3", "prep i", "prep ii", "prep iii"].includes(className)) {
      return "KG Head";
    } else if (["1", "2", "3", "4", "5"].includes(className)) {
      return "Headmistress";
    } else if (["6", "7", "8", "9", "10", "11"].includes(className)) {
      return "Principal";
    }
    return "Head";
  };

  const fetchMarks = async (retryCount = 0) => {
    const currentStudent = students[currentStudentIndex];
    if (!currentStudent?.student_id) {
      console.warn("No valid student_id found for fetching marks");
      return;
    }

    setCoMarksLoading(true);
    setTraitRemarksLoading(true);
    setCoMarksError(null);
    setTraitRemarksError(null);

    try {
      const response = await axios.get(`${API_URL}/student_marks`, {
        params: {
          student_id: currentStudent.student_id,
          stream: ["9", "10"].includes(selectedClass) ? selectedStream : undefined,
          test: selectedTest,
        },
        headers: { "X-User-Role": userRole || "teacher", "Cache-Control": "no-store" },
      });

      const marksData = response.data || {};
      console.log(`[Class ${selectedClass}] Fetched Marks Data for ${currentStudent.student_id}:`, JSON.stringify(marksData, null, 2));

      if (!marksData.scholastic_marks && !marksData.co_scholastic_marks && !marksData.traitRemarks) {
        const headRole = getRequiredHead(selectedClass);
        toast.warn(`No marks found for ${selectedTest}. Contact the ${headRole}.`);
        setMarks({});
        setMaxMarks({});
        setCoMarks({});
        setTraitRemarks({});
        setRemarks({});
        setCoMarksLoading(false);
        setTraitRemarksLoading(false);
        return;
      }

      const detectedStream = determineStream(marksData) || selectedStream || "science";
      setSelectedStream(detectedStream);
      console.log(`[Class ${selectedClass}] Detected stream for ${currentStudent.student_id}: ${detectedStream}`);

      setIsTeacherSubmitted(!!marksData.is_teacher_submitted || marksData.status === "teacher_added");
      setIsHeadSubmitted(marksData.is_submitted && marksData.status === "senior_edited");
      setIsAdminFinalized(marksData.is_submitted && marksData.status === "admin_finalized");

      const newMarks = {};
      const newMaxMarks = {};
      const newCoMarks = {};
      const newTraitRemarks = {};
      const newRemarks = {};
      const testType = selectedTest.toLowerCase().includes("unit") ? "unitTest" : "termExam";

      Object.entries(marksData.scholastic_marks || {}).forEach(([subjectSpec, value]) => {
        let normalizedSubjectSpec = subjectSpec.replace(new RegExp(`^${currentStudent.student_id}-`), "").replace("-obtained", "");
        const matchedSubject = filteredSubjects.find((s) => s.id.trim().toLowerCase() === normalizedSubjectSpec.trim().toLowerCase());
        if (matchedSubject) {
          newMarks[`${currentStudent.student_id}-${matchedSubject.id}-obtained`] = value || "";
          const max = marksData.max_marks?.[normalizedSubjectSpec] ?? matchedSubject.maxMarks?.[testType] ?? 100;
          newMaxMarks[`${currentStudent.student_id}-${matchedSubject.id}`] = max;
        } else {
          console.warn(`Unrecognized subject ${normalizedSubjectSpec} for ${currentStudent.student_id} - Skipping`);
        }
      });

      Object.entries(marksData.co_scholastic_marks || {}).forEach(([coSubjectName, value]) => {
        let normalizedCoSubjectName = coSubjectName.replace(new RegExp(`^${currentStudent.student_id}-`), "").replace("-obtained", "");
        const coSubject = coSubjects.find((s) => s.id.trim().toLowerCase() === normalizedCoSubjectName.trim().toLowerCase());
        if (coSubject) {
          newCoMarks[`${currentStudent.student_id}-${coSubject.id}-obtained`] = value ? value.toUpperCase() : "";
          console.log(`[Class ${selectedClass}] Set co-scholastic ${coSubject.id} for ${currentStudent.student_id}: ${value}`);
        } else {
          console.warn(`Co-scholastic subject ${normalizedCoSubjectName} not found in class ${selectedClass}`);
        }
      });

      Object.entries(marksData.traitRemarks || {}).forEach(([traitId, value]) => {
        let normalizedTraitId = traitId.replace(new RegExp(`^${currentStudent.student_id}-`), "");
        const trait = traitObservations.find((t) => t.id.trim().toLowerCase() === normalizedTraitId.trim().toLowerCase());
        if (trait) {
          newTraitRemarks[`${currentStudent.student_id}-${trait.id}`] = value ? value.toUpperCase() : "";
          console.log(`[Class ${selectedClass}] Set trait ${trait.id} for ${currentStudent.student_id}: ${value}`);
        } else {
          console.warn(`Trait ${normalizedTraitId} not found in class ${selectedClass}`);
        }
      });

      newRemarks[currentStudent.student_id] = marksData.remarks || "";
      console.log(`[Class ${selectedClass}] Set remarks for ${currentStudent.student_id}:`, marksData.remarks);

      setMarks(newMarks);
      setMaxMarks(newMaxMarks);
      setCoMarks(newCoMarks);
      setTraitRemarks(newTraitRemarks);
      setRemarks(newRemarks);

      setAllStudentsData((prev) => ({
        ...prev,
        [currentStudent.student_id]: {
          marks: newMarks,
          maxMarks: newMaxMarks,
          coMarks: newCoMarks,
          traitRemarks: newTraitRemarks,
          remarks: newRemarks,
          isTeacherSubmitted: !!marksData.is_teacher_submitted || marksData.status === "teacher_added",
          isHeadSubmitted: marksData.is_submitted && marksData.status === "senior_edited",
          isAdminFinalized: marksData.is_submitted && marksData.status === "admin_finalized",
          stream: detectedStream,
        },
      }));

      setCoMarksLoading(false);
      setTraitRemarksLoading(false);
    } catch (error) {
      console.error(`[Class ${selectedClass}] Failed to fetch marks:`, error);
      const headRole = getRequiredHead(selectedClass);
      if (retryCount < 2) {
        console.log(`Retrying fetchMarks (attempt ${retryCount + 1})...`);
        setTimeout(() => fetchMarks(retryCount + 1), 1000);
      } else {
        setCoMarksError(`Failed to fetch co-scholastic marks for ${selectedTest}. Contact the ${headRole}.`);
        setTraitRemarksError(`Failed to fetch trait remarks for ${selectedTest}. Contact the ${headRole}.`);
        toast.error(error.response?.data?.message || `Failed to fetch marks for ${selectedTest}. Contact the ${headRole}.`);
        setCoMarksLoading(false);
        setTraitRemarksLoading(false);
      }
    }
  };

  const fetchMarksForAllStudents = async () => {
    if (!students.length) return;

    setCoMarksLoading(true);
    setTraitRemarksLoading(true);
    setCoMarksError(null);
    setTraitRemarksError(null);

    const newAllStudentsData = {};
    const testType = selectedTest.toLowerCase().includes("unit") ? "unitTest" : "termExam";

    for (const student of students) {
      if (!student.student_id) {
        console.warn(`[Class ${selectedClass}] No valid student_id for ${student.student_name}`);
        continue;
      }

      try {
        const response = await axios.get(`${API_URL}/student_marks`, {
          params: {
            student_id: student.student_id,
            stream: ["9", "10"].includes(selectedClass) ? (student.stream || selectedStream) : undefined,
            test: selectedTest,
          },
          headers: { "X-User-Role": userRole || "teacher", "Cache-Control": "no-store" },
        });

        const marksData = response.data || {};
        console.log(`[Class ${selectedClass}] Fetched Marks Data for ${student.student_id}:`, JSON.stringify(marksData, null, 2));

        if (!marksData.scholastic_marks && !marksData.co_scholastic_marks && !marksData.traitRemarks) {
          console.warn(`[Class ${selectedClass}] No marks found for ${student.student_name} for test ${selectedTest}`);
          continue;
        }

        const detectedStream = determineStream(marksData) || (["9", "10"].includes(selectedClass) ? (student.stream || selectedStream || "science") : "science");
        console.log(`[Class ${selectedClass}] Detected stream for ${student.student_id}: ${detectedStream}`);

        const filteredSubjects = getFilteredSubjects(detectedStream);
        console.log(`[Class ${selectedClass}] Filtered subjects for ${student.student_id} (stream: ${detectedStream}):`, filteredSubjects.map(s => ({ id: s.id, name: s.name })));

        const newMarks = {};
        const newMaxMarks = {};
        const newCoMarks = {};
        const newTraitRemarks = {};
        const newRemarks = {};

        Object.entries(marksData.scholastic_marks || {}).forEach(([subjectSpec, value]) => {
          let normalizedSubjectSpec = subjectSpec.replace(new RegExp(`^${student.student_id}-`), "").replace("-obtained", "");
          const matchedSubject = filteredSubjects.find((s) => s.id.trim().toLowerCase() === normalizedSubjectSpec.trim().toLowerCase());
          if (matchedSubject) {
            newMarks[`${student.student_id}-${matchedSubject.id}-obtained`] = value === 0 ? "" : value || "";
            const max = marksData.max_marks?.[normalizedSubjectSpec] ?? matchedSubject.maxMarks?.[testType] ?? 100;
            newMaxMarks[`${student.student_id}-${matchedSubject.id}`] = max;
          } else {
            console.warn(`[Class ${selectedClass}] No matching subject found for ${student.student_id}, subjectSpec: ${subjectSpec}, normalized: ${normalizedSubjectSpec}`);
          }
        });

        Object.entries(marksData.co_scholastic_marks || {}).forEach(([coSubjectName, value]) => {
          let normalizedCoSubjectName = coSubjectName.replace(new RegExp(`^${student.student_id}-`), "").replace("-obtained", "");
          const coSubject = coSubjects.find((s) => s.id.trim().toLowerCase() === normalizedCoSubjectName.trim().toLowerCase());
          if (coSubject) {
            newCoMarks[`${student.student_id}-${coSubject.id}-obtained`] = value ? value.toUpperCase() : "";
          } else {
            console.warn(`[Class ${selectedClass}] No matching co-scholastic subject found for ${student.student_id}, coSubjectName: ${coSubjectName}`);
          }
        });

        Object.entries(marksData.traitRemarks || {}).forEach(([traitId, value]) => {
          let normalizedTraitId = traitId.replace(new RegExp(`^${student.student_id}-`), "");
          const trait = traitObservations.find((t) => t.id.trim().toLowerCase() === normalizedTraitId.trim().toLowerCase());
          if (trait) {
            newTraitRemarks[`${student.student_id}-${trait.id}`] = value ? value.toUpperCase() : "";
          } else {
            console.warn(`[Class ${selectedClass}] No matching trait found for ${student.student_id}, traitId: ${traitId}`);
          }
        });

        newRemarks[student.student_id] = marksData.remarks || "No remarks provided";

        newAllStudentsData[student.student_id] = {
          marks: newMarks,
          maxMarks: newMaxMarks,
          coMarks: newCoMarks,
          traitRemarks: newTraitRemarks,
          remarks: newRemarks,
          isTeacherSubmitted: !!marksData.is_teacher_submitted || marksData.status === "teacher_added",
          isHeadSubmitted: marksData.is_submitted && marksData.status === "senior_edited",
          isAdminFinalized: marksData.is_submitted && marksData.status === "admin_finalized",
          stream: detectedStream,
        };
        console.log(`[Class ${selectedClass}] Stored data for ${student.student_id} in allStudentsData:`, JSON.stringify(newAllStudentsData[student.student_id], null, 2));
      } catch (error) {
        console.error(`[Class ${selectedClass}] Failed to fetch marks for ${student.student_id}:`, error);
        const headRole = getRequiredHead(selectedClass);
        toast.warn(`Failed to fetch marks for ${student.student_name} for ${selectedTest}. Contact the ${headRole}.`);
      }
    }

    setAllStudentsData(newAllStudentsData);
    console.log(`[Class ${selectedClass}] Final allStudentsData:`, JSON.stringify(newAllStudentsData, null, 2));
    setCoMarksLoading(false);
    setTraitRemarksLoading(false);
  };

  useEffect(() => {
    const fetchUserRole = async () => {
      const role = localStorage.getItem("userRole") || "admin";
      setUserRole(role);
    };
    fetchUserRole();
  }, []);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setLoading(true);
        if (!selectedClass || !selectedSection) {
          toast.warning("Please select both class and section");
          setStudents([]);
          return;
        }

        const response = await axios.get(`${API_URL}/students`, {
          params: { className: selectedClass, section: selectedSection, stream: ["9", "10"].includes(selectedClass) ? selectedStream : undefined },
          headers: { "X-User-Role": userRole || "teacher", "Cache-Control": "no-store" },
        });

        let studentData = Array.isArray(response.data.data) ? response.data.data : response.data;
        const filtered = studentData.filter(
          (student) =>
            student.class?.trim().toLowerCase() === selectedClass.trim().toLowerCase() &&
            student.section?.toUpperCase().trim() === selectedSection.toUpperCase().trim()
        );

        if (filtered.some((student) => !student.student_id)) {
          toast.error("Some students have invalid IDs. Contact the administrator.");
        }

        const sortedStudents = filtered.sort((a, b) =>
          (a.student_name || "").toLowerCase().localeCompare((b.student_name || "").toLowerCase())
        );

        setStudents(sortedStudents);
        setCurrentStudentIndex(0);

        await fetchMarksForAllStudents();
      } catch (error) {
        const headRole = getRequiredHead(selectedClass);
        toast.error(error.response?.data?.message || `Failed to load students. Contact the ${headRole}.`);
        setStudents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, [selectedClass, selectedSection, userRole, selectedTest, selectedStream]);

  useEffect(() => {
    if (students.length > 0 && ["teacher", "head", "admin"].includes(userRole || "teacher")) {
      console.log("Fetching marks for student:", students[currentStudentIndex]?.student_id);
      fetchMarks();
    } else {
      setMarks({});
      setMaxMarks({});
      setCoMarks({});
      setTraitRemarks({});
      setRemarks({});
      setCoMarksLoading(false);
      setTraitRemarksLoading(false);
      setCoMarksError(null);
      setTraitRemarksError(null);
    }
  }, [currentStudentIndex, students, userRole, selectedStream, selectedTest]);

  const canEdit = () => {
    return userRole === "admin" && !isAdminFinalized;
  };

  const handleMarksChange = (studentId, subjectId, value) => {
    if (!canEdit()) {
      const headRole = getRequiredHead(selectedClass);
      toast.error(`Cannot edit marks. Marks are finalized. Contact the ${headRole}.`);
      return;
    }

    const subject = subjects.find((s) => s.id === subjectId.split("-obtained")[0]);
    if (!subject) {
      toast.error("Invalid subject selected.");
      return;
    }

    const testType = selectedTest.toLowerCase().includes("unit") ? "unitTest" : "termExam";
    const maxMarks = subject.maxMarks?.[testType] ?? subject.maxMarks ?? 100;
    const isPrepClass = ["prep 1", "prep 2", "prep 3", "prep i", "prep ii", "prep iii"].includes(selectedClass.toLowerCase().trim());
    const validGrades = ["A+", "A", "B+", "B", "C", "AB", ""];

    if (isPrepClass) {
      const normalizedValue = value ? value.toUpperCase() : "";
      if (validGrades.includes(normalizedValue)) {
        setMarks((prev) => ({
          ...prev,
          [`${studentId}-${subject.id}-obtained`]: normalizedValue,
        }));
        setAllStudentsData((prev) => ({
          ...prev,
          [studentId]: {
            ...prev[studentId],
            marks: {
              ...prev[studentId]?.marks,
              [`${studentId}-${subject.id}-obtained`]: normalizedValue,
            },
          },
        }));
        console.log(`[Class ${selectedClass}] Updated scholastic marks[${studentId}-${subject.id}-obtained]: ${normalizedValue}`);
      } else {
        toast.error(`Invalid grade for ${subject.name}. Use A+, A, B+, B, C, AB, or leave empty.`);
      }
    } else {
      const numValue = parseFloat(value);
      if (value === "" || (!isNaN(numValue) && numValue >= 0 && numValue <= maxMarks)) {
        setMarks((prev) => ({
          ...prev,
          [`${studentId}-${subject.id}-obtained`]: value,
        }));
        setAllStudentsData((prev) => ({
          ...prev,
          [studentId]: {
            ...prev[studentId],
            marks: {
              ...prev[studentId]?.marks,
              [`${studentId}-${subject.id}-obtained`]: value,
            },
          },
        }));
        console.log(`[Class ${selectedClass}] Updated scholastic marks[${studentId}-${subject.id}-obtained]: ${value === "" ? "empty" : numValue}`);
      } else {
        toast.error(`Marks for ${subject.name} must be between 0 and ${maxMarks}.`);
      }
    }
  };

  const handleCoMarksChange = (studentId, coSubjectId, value) => {
    if (!canEdit()) {
      const headRole = getRequiredHead(selectedClass);
      toast.error(`Cannot edit co-scholastic marks. Marks are finalized. Contact the ${headRole}.`);
      return;
    }

    const key = `${studentId}-${coSubjectId.split("-obtained")[0]}-obtained`;
    const validGrades = ["A+", "A", "B+", "B", "C", "AB", ""];
    const normalizedValue = value ? value.toUpperCase() : "";

    console.log(`[Class ${selectedClass}] Attempting to set co-scholastic ${key}: ${value} (normalized: ${normalizedValue})`);

    if (validGrades.includes(normalizedValue)) {
      setCoMarks((prev) => {
        const newCoMarks = { ...prev, [key]: normalizedValue };
        console.log(`[Class ${selectedClass}] Updated coMarks[${key}]: ${normalizedValue}`);
        return newCoMarks;
      });
      setAllStudentsData((prev) => ({
        ...prev,
        [studentId]: {
          ...prev[studentId],
          coMarks: {
            ...prev[studentId]?.coMarks,
            [key]: normalizedValue,
          },
        },
      }));
    } else {
      toast.error(`Invalid grade for co-scholastic subject. Use A+, A, B+, B, C, AB, or leave empty.`);
      console.log(`[Class ${selectedClass}] Invalid co-scholastic grade: ${value}`);
    }
  };

  const handleTraitRemarksChange = (studentId, traitId, value) => {
    if (!canEdit()) {
      const headRole = getRequiredHead(selectedClass);
      toast.error(`Cannot edit trait remarks. Marks are finalized. Contact the ${headRole}.`);
      return;
    }

    const validGrades = ["A+", "A", "B+", "B", "C", "AB", "D", ""];
    const normalizedValue = value ? value.toUpperCase() : "";

    if (!validGrades.includes(normalizedValue)) {
      toast.error(`Invalid observation for ${traitId.replace("_", " ").toLowerCase()}. Use A+, A, B+, B, C, AB, D, or leave empty.`);
      return;
    }

    setTraitRemarks((prev) => {
      const newTraitRemarks = { ...prev, [`${studentId}-${traitId}`]: normalizedValue };
      console.log(`[Class ${selectedClass}] Updated traitRemarks[${studentId}-${traitId}]: ${normalizedValue}`);
      return newTraitRemarks;
    });
    setAllStudentsData((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        traitRemarks: {
          ...prev[studentId]?.traitRemarks,
          [`${studentId}-${traitId}`]: normalizedValue,
        },
      },
    }));
  };

  const handleRemarksChange = (studentId, value) => {
    if (!canEdit()) {
      const headRole = getRequiredHead(selectedClass);
      toast.error(`Cannot edit remarks. Marks are finalized. Contact the ${headRole}.`);
      return;
    }
    setRemarks((prev) => {
      const newRemarks = { ...prev, [studentId]: value };
      console.log(`[Class ${selectedClass}] Updated remarks[${studentId}]: ${value}`);
      return newRemarks;
    });
    setAllStudentsData((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        remarks: {
          ...prev[studentId]?.remarks,
          [studentId]: value,
        },
      },
    }));
  };

  const handleStreamChange = (e) => {
    const newStream = e.target.value;
    setSelectedStream(newStream);
    setMarks({});
    setMaxMarks({});
    setCurrentStudentIndex(0);
    console.log(`[Class ${selectedClass}] Stream changed to: ${newStream}`);
    fetchMarksForAllStudents();
  };

  const renderStreamDropdown = () => {
    if (!["9", "10"].includes(selectedClass)) {
      return null;
    }
    return (
      <div className="stream-dropdown">
        <label htmlFor="stream">Stream: </label>
        <select id="stream" value={selectedStream} onChange={handleStreamChange}>
          <option value="science">Science</option>
          <option value="commerce">Commerce</option>
        </select>
      </div>
    );
  };

  const handleSubmit = async () => {
    if (isSubmitting) {
      toast.warn("Submission in progress, please wait");
      return;
    }

    setIsSubmitting(true);
    try {
      const currentStudent = students[currentStudentIndex];
      const teacherPhone = localStorage.getItem("userPhone");

      const scholasticMarkEntries = subjects.map((s) => [
        s.id,
        marks[`${currentStudent.student_id}-${s.id}-obtained`] || "",
      ]);
      const coScholasticMarkEntries = coSubjects.map((cs) => [
        cs.id,
        coMarks[`${currentStudent.student_id}-${cs.id}-obtained`] || "",
      ]);

      const hasAnyMarks =
        scholasticMarkEntries.some(([, v]) => v !== "") ||
        coScholasticMarkEntries.some(([, v]) => v !== "");

      if (!hasAnyMarks) {
        toast.error("No marks entered to submit.");
        setIsSubmitting(false);
        return;
      }

      const payload = {
        student_id: currentStudent.student_id,
        class_name: selectedClass,
        stream: ["9", "10"].includes(selectedClass) ? selectedStream : undefined,
        teacher_phone: userRole === "admin" ? undefined : teacherPhone,
        scholastic_marks: Object.fromEntries(scholasticMarkEntries),
        co_scholastic_marks: Object.fromEntries(coScholasticMarkEntries),
        max_marks: Object.fromEntries(
          subjects.map((s) => [
            s.id,
            parseFloat(maxMarks[`${currentStudent.student_id}-${s.id}`]) || s.maxMarks || 50,
          ])
        ),
        traitRemarks: Object.fromEntries(
          traitObservations.map((t) => [
            t.id,
            traitRemarks[`${currentStudent.student_id}-${t.id}`] || "",
          ])
        ),
        remarks: remarks[currentStudent.student_id] || "",
        test_name: selectedTest,
      };

      console.log(
        `[Class ${selectedClass}] Submitting Payload for ${currentStudent.student_id}:`,
        JSON.stringify(payload, null, 2)
      );

      let endpoint =
        userRole === "admin" ? "/admin/update_marks" :
        userRole === "head" ? "/head/update_marks" :
        "/student_marks";

      const response = await axios.post(`${API_URL}${endpoint}`, payload, {
        headers: { "X-User-Role": userRole || "teacher", "Cache-Control": "no-store" },
      });

      console.log(
        `[Class ${selectedClass}] Submit Response for ${currentStudent.student_id}:`,
        JSON.stringify(response.data, null, 2)
      );

      toast.success(response.data.message || "Marks and remarks updated successfully");
      await fetchMarks();
    } catch (err) {
      console.error(`[Class ${selectedClass}] Submit Error:`, err);
      const headRole = getRequiredHead(selectedClass);
      const message = err.response?.data?.message || `Failed to update marks and remarks. Contact the ${headRole}.`;
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

 const calculateTotals = (studentId) => {
  if (!studentId) {
    return { totalMax: 0, totalScored: 0, percentage: 0, grade: "N/A" };
  }

  const studentData = allStudentsData[studentId] || {};
  const studentStream = studentData.stream?.toLowerCase() || "science";
  const scholasticSubjects = getFilteredSubjects(studentStream);

  let totalMax = 0;
  let totalScored = 0;

  const testType = selectedTest.toLowerCase().includes("unit") ? "unitTest" : "termExam";

  if (!Array.isArray(scholasticSubjects)) {
    console.error(`calculateTotals: scholasticSubjects is not an array for class ${selectedClass}:`, scholasticSubjects);
    return { totalMax: 0, totalScored: 0, percentage: 0, grade: "D" };
  }

  scholasticSubjects.forEach((subject) => {
    const key = `${studentId}-${subject.id}-obtained`;
    const value = studentData.marks?.[key] || "";

    // Max Marks Logic (especially for 6,7,8)
    let max = 100;
    if (["6", "7", "8"].includes(selectedClass.trim())) {
      const lowerId = (subject.id || "").toLowerCase().trim();
      const lowerName = (subject.name || "").toLowerCase().trim();
      const isThirdLanguage =
        lowerId.includes("iii") ||
        lowerId.includes("3rd") ||
        lowerName.includes("iii language") ||
        lowerName.includes("3rd language") ||
        lowerName.includes("third language") ||
        lowerName.includes("iii lang") ||
        lowerName === "iii" ||
        lowerName === "third lang";

      max = testType === "unitTest" 
        ? (isThirdLanguage ? 25 : 50)
        : (isThirdLanguage ? 50 : 100);
    } 
    else if (["9", "10"].includes(selectedClass.trim())) {
      max = testType === "unitTest" ? 50 : 100;
    } 
    else {
      max = parseFloat(subject.maxMarks?.[testType]) || parseFloat(subject.maxMarks) || 100;
    }

    const scored = (value === "" || value === "-" || value === "0") 
      ? 0 
      : parseFloat(value) || 0;

    totalMax += max;
    totalScored += scored;

    console.log(`[Class ${selectedClass}] ${subject.name}: Max=${max}, Scored=${scored}`);
  });

  const percentage = totalMax > 0 ? (totalScored / totalMax) * 100 : 0;

  // ✅ YOUR CORRECT GRADE LOGIC (Use this everywhere)
  const grade = 
    percentage >= 94 ? "A+" :
    percentage >= 84 ? "A" :
    percentage >= 69 ? "B+" :
    percentage >= 49 ? "B" :
    percentage >= 39 ? "C" : "D";

  console.log(`[Class ${selectedClass}] Totals for ${studentId}: ${totalScored}/${totalMax} → ${percentage.toFixed(2)}% → Grade = ${grade}`);

  return {
    totalMax,
    totalScored,
    percentage: Number(percentage.toFixed(2)),
    grade,
  };
};
const generatePDF = (student, doc = null, isLastStudent = true) => {
  const isSinglePDF = !doc;
  if (isSinglePDF) {
    doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  }
  const leftMargin = 20;
  const rightMargin = 210 - leftMargin;
  const studentId = student.student_id;
  const studentName = student.student_name;
  const studentData = allStudentsData[studentId] || {};
  const studentStream = studentData.stream?.toLowerCase() || "science";
  console.log(`[Class ${selectedClass}] Generating PDF for ${studentId} (${studentName}). Stream: ${studentStream}`);
  const scholasticSubjects = getFilteredSubjects(studentStream);
  const coScholasticSubjects = classSubjects[selectedClass]?.coScholastic || [];
  const traits = classSubjects[selectedClass]?.traitObservations || [];
  const testType = selectedTest.toLowerCase().includes("unit") ? "unitTest" : "termExam";
  console.log(`Selected Test: ${selectedTest}, Determined testType: ${testType}`);
  if (Object.keys(studentData).length === 0) {
    console.warn(`[Class ${selectedClass}] No data found for student ${studentId}`);
  }
  const totals = calculateTotals(studentId);
  const isPrepClass = ["prep i", "prep ii", "prep iii"].includes(selectedClass.toLowerCase().trim());
  const isClass678 = ["6", "7", "8"].includes(selectedClass.toLowerCase().trim());
  const isClass1to11 = /^[1-9]$|10|11/.test(selectedClass.trim());
  const isClass1to5 = ["1", "2", "3", "4", "5"].includes(selectedClass.toLowerCase().trim());
  const isClass1to5_9_10 = ["1", "2", "3", "4", "5", "9", "10"].includes(selectedClass.trim());
  let currentY = isSinglePDF || doc.getNumberOfPages() === 1 ? 32 : 32;
  doc.setFontSize(14);
  doc.setFont(undefined, "bold");
     // Dynamic test heading - ONLY PREP 1,2,3 CHANGED
  const getTestDisplayName = () => {
    if (isPrepClass) {
      switch (selectedTest) {
        case "I Unit Test":
          return `I Unit Assessment (2025-26)`;
        case "II Unit Test":
          return `II Unit Assessment (2025-26)`;
        case "I Term Examination":
          return `I Term Assessment (2025-26)`;
        case "Annual Examination":
          return `Final Assessment (2026-27)`;   // ← Only for Prep 1, Prep 2, Prep 3
        default:
          return `${selectedTest} (2025-26)`;
      }
    } else {
      // All other classes (1 to 10) remain unchanged
      return `${selectedTest} (2025-26)`;
    }
  };

  const heading = getTestDisplayName();
  doc.text(heading, 105, currentY, { align: "center" });
  currentY += 5;
  doc.setFontSize(12);
  doc.setFont(undefined, "normal");
  doc.setFont(undefined, "bold");
  doc.text(`Name: ${studentName}`, leftMargin, currentY);
  doc.setFont(undefined, "normal");
  doc.text(`Class: ${selectedClass} - ${selectedSection}`, rightMargin, currentY, { align: "right" });
  currentY += 5;
  doc.setFont(undefined, "bold");
  doc.text("Scholastic", leftMargin, currentY);
  currentY += 2;
  doc.setFont(undefined, "normal");

 // Updated Grade Logic for individual subjects in PDF
// 94 and above = A+, 84 to 93.99 = A  (as per your correct logic)
const getGrade = (obtained, max) => {
  if (!obtained || obtained === "" || obtained === "-" || max === 0) {
    return "";
  }

  const numObtained = parseFloat(obtained);
  if (isNaN(numObtained) || numObtained > max) {
    return obtained;   // Return original if teacher entered grade directly (A+, B etc.)
  }

  const percentage = (numObtained / max) * 100;

  // ✅ YOUR FINAL CORRECT LOGIC
  return percentage >= 94 ? "A+" :
         percentage >= 84 ? "A" :
         percentage >= 69 ? "B+" :
         percentage >= 49 ? "B" :
         percentage >= 39 ? "C" : "D";
};

    const scholasticData = [];
  if (isPrepClass) {
    scholasticSubjects.forEach((subject) => {
      const subjectId = subject.id;
      const subjectName = subject.name || "N/A";
      const subjectSpecs = subject.specs || [];
      if (subjectSpecs.length > 0) {
        subjectSpecs.forEach((spec, index) => {
          const key = `${studentId}-${subjectId}-${spec.id}-obtained`;
          const obtained = studentData.marks?.[key] ?? "-";
          console.log(`[Prep] Subject: ${subjectName}, Spec: ${spec.name}, Obtained: ${obtained}`);
          scholasticData.push([
            index === 0 ? subjectName : "",
            spec.name || spec.id || "Spec",
            obtained === "0" ? "" : obtained,
          ]);
        });
      } else {
        const key = `${studentId}-${subjectId}-obtained`;
        const obtained = studentData.marks?.[key] ?? "-";
        console.log(`[Prep] Subject: ${subjectName}, Obtained: ${obtained}`);
        scholasticData.push([
          subjectName,
          subject.spec || "",
          obtained === "0" ? "" : obtained,
        ]);
      }
    });
  }  else if (["6", "7", "8", "9", "10"].includes(selectedClass.trim())) {
  scholasticSubjects.forEach((subject) => {
    const subjectId = subject.id;
    const subjectName = subject.name || "N/A";

    const obtainedKey = `${studentId}-${subjectId}-obtained`;
    const obtained = studentData.marks?.[obtainedKey] ?? "-";

    // ==================== MAX MARKS LOGIC ====================
    let max = 100;

    if (["6", "7", "8"].includes(selectedClass.trim())) {
      const lowerId = (subjectId || "").toLowerCase().trim();
      const lowerName = (subjectName || "").toLowerCase().trim();

      const isThirdLanguage =
        lowerId.includes("iii") ||
        lowerId.includes("3rd") ||
        lowerName.includes("iii language") ||
        lowerName.includes("3rd language") ||
        lowerName.includes("third language") ||
        lowerName.includes("iii lang") ||
        lowerName === "iii" ||
        lowerName === "third lang";

      if (testType === "unitTest") {
        max = isThirdLanguage ? 25 : 50;
      } else {
        // Annual Examination
        max = isThirdLanguage ? 50 : 100;
      }
    } else {
      // Class 9 & 10
      const maxFromSubject = subject.maxMarks?.[testType] || (testType === "unitTest" ? 50 : 100);
      const maxKey = `${studentId}-${subjectId}`;
      const maxFromStudent = parseFloat(studentData.maxMarks?.[maxKey]);
      max = maxFromStudent && !isNaN(maxFromStudent) && maxFromStudent > 0
        ? maxFromStudent
        : maxFromSubject;
    }

    if (!max || isNaN(max) || max <= 0) max = 100;

    // ==================== GRADE CALCULATION ====================
    let gradeColumn = "";
    if (obtained !== "-" && obtained !== "" && !isNaN(parseFloat(obtained))) {
      // Use proper percentage-based grade (88+ = A+)
      gradeColumn = getGrade(obtained, max);
    } else {
      // If teacher entered grade directly (A+, A, B+ etc.)
      gradeColumn = obtained;
    }

    scholasticData.push([
      subjectName,
      obtained === "0" ? "" : max.toString(),   // Max Marks
      obtained === "0" ? "" : obtained,         // Marks Obtained
      gradeColumn                               // Grade
    ]);
  });
}else {
    const groupedSubjects = scholasticSubjects.reduce((acc, subject) => {
      const subjectName = subject.name || "Unnamed";
      const subjectId = subject.id;
      if (!acc[subjectName]) {
        acc[subjectName] = { specs: [], totalMax: 0, totalObtained: 0, hasMissing: false };
      }
      const subjectSpecs = subject.specs?.length
        ? subject.specs
        : [{ id: subjectId, name: subject.spec, maxMarks: subject.maxMarks?.[testType] || 100 }];
      subjectSpecs.forEach((spec) => {
        const rawSpecId = spec.id || subjectId;
        const key = `${studentId}-${rawSpecId}-obtained`;
        const maxKey = `${studentId}-${rawSpecId}`;
        const obtainedRaw = studentData.marks?.[key];
        const obtained = obtainedRaw ?? "-";
        const max = parseFloat(studentData.maxMarks?.[maxKey]) || spec.maxMarks || subject.maxMarks?.[testType] || 100;
        acc[subjectName].specs.push({
          specName: spec.name || rawSpecId,
          obtained,
          max,
        });
        acc[subjectName].totalMax += max;
        if (obtained !== "-") {
          acc[subjectName].totalObtained += parseFloat(obtained) || 0;
        } else {
          acc[subjectName].hasMissing = true;
        }
      });
      return acc;
    }, {});
    Object.entries(groupedSubjects).forEach(([subjectName, { specs, totalMax, totalObtained, hasMissing }]) => {
      const totalGrade = hasMissing ? "" : getGrade(totalObtained, totalMax);
      specs.forEach((spec, index) => {
        const isFirst = index === 0;
        console.log(`[1-5] Subject: ${subjectName}, Spec: ${spec.specName}, Max: ${spec.max}, Obtained: ${spec.obtained}, Total: ${isFirst ? (hasMissing ? "" : totalObtained) : ""}, Grade: ${isFirst ? totalGrade : ""}`);
        scholasticData.push([
          isFirst ? subjectName : "",
          spec.specName,
          spec.obtained === "0" ? "" : spec.max.toString(),
          spec.obtained === "0" ? "" : spec.obtained,
          isFirst ? (hasMissing ? "" : totalObtained.toString()) : "",
          isFirst ? totalGrade : "",
        ]);
      });
    });
  }

 autoTable(doc, {
  startY: currentY,
  head: isPrepClass
    ? [["Subjects", "Assessment Specification", "Grade"]]
    : ["6", "7", "8", "9", "10"].includes(selectedClass.trim())
    ? [["Subjects", "Max Marks", "Marks Obtained", "Grade"]]
    : [["Subjects", "Assessment Specification", "Max Marks", "Marks Obtained", "Total Marks", "Grade"]],
  body: scholasticData,
  styles: {
    fontSize: isPrepClass ? 10.5 : 10,
    textColor: 0,
    valign: "middle",
    halign: "center",
    lineColor: [50, 50, 50],
    lineWidth: { top: 0.2, right: 0.2, bottom: 0.2, left: 0.2 }, // Changed to object
    ...(isPrepClass && {
      minCellHeight: 7,
      cellPadding: { top: 1, right: 2, bottom: 1, left: 2 },
    }),
    ...(["6", "7", "8", "9", "10"].includes(selectedClass.trim()) && {
      minCellHeight: 8,
      cellPadding: { top: 1, right: 2, bottom: 1, left: 2 },
    }),
    ...(!isPrepClass && !["6", "7", "8", "9", "10"].includes(selectedClass.trim()) && {
      minCellHeight: 5,
      cellPadding: { top: 0.5, right: 1.5, bottom: 0.5, left: 1.5 },
    }),
  },
  headStyles: {
    textColor: 0,
    fontStyle: "bold",
    halign: "center",
    valign: "middle",
    lineColor: [50, 50, 50],
    lineWidth: { top: 0.3, right: 0.3, bottom: 0.3, left: 0.3 }, // Changed to object
    fillColor: false,
    fontSize: isPrepClass ? 10.8 : 10,
  },
  columnStyles: {
    ...(isPrepClass && {
      0: { halign: "center", cellWidth: 50 },
      1: { halign: "left", cellWidth: 90 },
      2: { fontStyle: "bold", halign: "center", cellWidth: 30 },
    }),
    ...(["6", "7", "8", "9", "10"].includes(selectedClass.trim()) && {
      0: { halign: "center" },
      1: { fontStyle: "bold", halign: "center" },
      2: { fontStyle: "bold", halign: "center" },
      3: { fontStyle: "bold", halign: "center" },
    }),
    ...(!isPrepClass && !["6", "7", "8", "9", "10"].includes(selectedClass.trim()) && {
      0: { cellWidth: 33, halign: "center", valign: "middle" },
      1: { cellWidth: 60 },
      2: { cellWidth: 17, fontStyle: "bold", halign: "center" },
      3: { cellWidth: 22, fontStyle: "bold", halign: "center" },
      4: { cellWidth: 17, fontStyle: "bold", halign: "center" },
      5: { cellWidth: 14, fontStyle: "bold", halign: "center" },
    }),
  },
  theme: "grid",
  tableWidth: rightMargin - leftMargin,
  margin: { left: leftMargin },
  didDrawPage: (data) => {
    currentY = data.cursor.y + 3;
  },
  didParseCell: function (data) {
    const isOldFormat = !["6", "7", "8", "9", "10"].includes(selectedClass.trim());

    if (data.section === "body" && isOldFormat) {
      const rowData = scholasticData[data.row.index];
      const totalRows = data.table.body.length;
      const isLastRow = data.row.index === totalRows - 1;

      if (rowData) {
        // ✅ Subjects column (index 0)
        if (data.column.index === 0) {
          if (rowData[0] === "" || rowData[0] == null) {
            data.cell.styles.lineWidth = { top: 0, right: 0.2, bottom: 0, left: 0.2 };
          } else {
            data.cell.styles.lineWidth = { top: 0.2, right: 0.2, bottom: isLastRow ? 0.2 : 0, left: 0.2 };
            data.cell.styles.valign = "middle";
          }
        }

        // ✅ Total column (index 4)
        if (data.column.index === 4) {
          if (rowData[0] === "" || rowData[0] == null) {
            data.cell.styles.lineWidth = { top: 0, right: 0.2, bottom: 0, left: 0.2 };
          } else {
            data.cell.styles.lineWidth = { top: 0.2, right: 0.2, bottom: isLastRow ? 0.2 : 0, left: 0.2 };
            data.cell.styles.valign = "middle";
          }
        }

        // ✅ Grade column (index 5)
        if (data.column.index === 5) {
          if (rowData[0] === "" || rowData[0] == null) {
            data.cell.styles.lineWidth = { top: 0, right: 0.2, bottom: 0, left: 0.2 };
          } else {
            data.cell.styles.lineWidth = { top: 0.2, right: 0.2, bottom: isLastRow ? 0.2 : 0, left: 0.2 };
            data.cell.styles.valign = "middle";
          }
        }

        // Ensure last row has bottom border for all columns
        if (isLastRow) {
          data.cell.styles.lineWidth.bottom = 0.2; // This should now work with object
        }
      }
    }
  },
});

  if (isClass678) {
    const coX = leftMargin;
    const traitX = 110;
    const coBody = coScholasticSubjects.map((c) => [
      c.name || "N/A",
      studentData.coMarks?.[`${studentId}-${c.id}-obtained`] || "-",
    ]);
    const traitBody = traits.map((t) => [
      t.name || "N/A",
      studentData.traitRemarks?.[`${studentId}-${t.id}`] || "-",
    ]);
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    if (selectedClass.trim().toLowerCase() === "6","7","8") {
      currentY += 5;
    }
    doc.text("Co-Scholastic", coX, currentY);
    doc.text("Observations", traitX, currentY);
    currentY += 5;
    
    autoTable(doc, {
      startY: currentY,
      head: [["Subjects", "Grade"]],
      body: coBody,
      styles: {
        fontSize: 10,
        textColor: 0,
        halign: "center",
        lineColor: [50, 50, 50],
        lineWidth: 0.2,
      },
      headStyles: {
        textColor: 0,
        fontStyle: "bold",
        halign: "center",
        lineColor: [50, 50, 50],
        lineWidth: 0.3,
        fillColor: false,
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "center", fontStyle: "bold" },
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 0) {
          data.cell.styles.fontStyle = "normal";
        }
      },
      theme: "grid",
      tableWidth: 80,
      margin: { left: coX },
      pageBreak: "avoid",
    });
    autoTable(doc, {
      startY: currentY,
      head: [["Subjects", "Grade"]],
      body: traitBody,
      styles: {
        fontSize: 10,
        textColor: 0,
        halign: "center",
        lineColor: [50, 50, 50],
        lineWidth: 0.2,
      },
      headStyles: {
        textColor: 0,
        fontStyle: "bold",
        halign: "center",
        lineColor: [50, 50, 50],
        lineWidth: 0.3,
        fillColor: false,
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "center", fontStyle: "bold" },
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 0) {
          data.cell.styles.fontStyle = "normal";
        }
      },
      theme: "grid",
      tableWidth: 80,
      margin: { left: traitX },
      pageBreak: "avoid",
      didDrawPage: (data) => {
        currentY = data.cursor.y + 3;
      },
    });
  } else if (["1", "2", "3", "4", "5", "9", "10"].includes(selectedClass.trim())) {
    const traitX = leftMargin;
    const allTraits = traits.map((t) => ({
      name: t.name || "N/A",
      value: studentData.traitRemarks?.[`${studentId}-${t.id}`] || "-",
    }));
    const mainTraits = allTraits.filter((t) => t.name.toLowerCase() !== "supw");
    const supwTrait = allTraits.find((t) => t.name.toLowerCase() === "supw");
    const traitBody = mainTraits.map((t) => [t.name, t.value]);
    const supwBody = supwTrait ? [[supwTrait.name, supwTrait.value]] : [];
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text("Observations", traitX, currentY + 5);
    currentY += 10;
    let minCellHeight = null;
    let cellPadding = null;
    switch (selectedClass.trim()) {
      case "3":
        minCellHeight = 7;
        cellPadding = { top: 1, bottom: 1, left: 2, right: 2 };
        break;
      case "4":
        minCellHeight = 6;
        cellPadding = { top: 1, bottom: 1, left: 1.5, right: 1.5 };
        break;
      case "5":
        minCellHeight = 6;
        cellPadding = { top: 0.5, bottom: 0.5, left: 1, right: 1 };
        break;
      case "9":
      case "10":
        minCellHeight = 8;
        cellPadding = { top: 2, bottom: 2, left: 2, right: 2 };
        break;
      default:
        minCellHeight = 8;
        cellPadding = { top: 1, bottom: 1, left: 2, right: 2 };
        break;
    }
    autoTable(doc, {
      startY: currentY,
      head: [["Subjects", "Grade"]],
      body: traitBody,
      styles: {
        fontSize: 10,
        textColor: 0,
        halign: "center",
        lineColor: [50, 50, 50],
        lineWidth: 0.2,
        ...(minCellHeight && { minCellHeight }),
        ...(cellPadding && { cellPadding }),
      },
      headStyles: {
        textColor: 0,
        fontStyle: "bold",
        halign: "center",
        lineColor: [50, 50, 50],
        lineWidth: 0.3,
        fillColor: false,
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "center", fontStyle: "bold" },
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 0) {
          data.cell.styles.fontStyle = "normal";
        }
      },
      theme: "grid",
      tableWidth: 80,
      margin: { left: traitX },
      pageBreak: "avoid",
      didDrawPage: (data) => {
        currentY = data.cursor.y + 2;
      },
    });
    if (supwBody.length > 0) {
      currentY += 2;
      autoTable(doc, {
        startY: currentY,
        head: [["Subjects", "Grade"]],
        body: supwBody,
        showHead: "never",
        styles: {
          fontSize: 10,
          textColor: 0,
          halign: "center",
          lineColor: [50, 50, 50],
          lineWidth: 0.2,
          ...(minCellHeight && { minCellHeight }),
          ...(cellPadding && { cellPadding }),
        },
        headStyles: {
          textColor: 0,
          fontStyle: "bold",
          halign: "center",
          lineColor: [50, 50, 50],
          lineWidth: 0.3,
          fillColor: false,
        },
        columnStyles: {
          0: { halign: "left", cellWidth: 60 },
          1: { halign: "center", fontStyle: "bold", cellWidth: 20 },
        },
        didParseCell: (data) => {
          if (data.section === "body" && data.column.index === 0) {
            data.cell.styles.fontStyle = "normal";
          }
        },
        theme: "grid",
        tableWidth: 80,
        margin: { left: traitX },
        pageBreak: "avoid",
        didDrawPage: (data) => {
          currentY = data.cursor.y + 2;
        },
      });
    }
  } else {
    if (!isPrepClass && coScholasticSubjects.length > 0) {
      autoTable(doc, {
        startY: currentY,
        head: [["Co-Scholastic", "Grade"]],
        body: coScholasticSubjects.map((c) => [
          c.name || "N/A",
          studentData.coMarks?.[`${studentId}-${c.id}-obtained`] || "-",
        ]),
        styles: {
          fontSize: 10,
          textColor: 0,
          lineColor: [50, 50, 50],
          lineWidth: 0.2,
        },
        headStyles: {
          textColor: 0,
          fontStyle: "bold",
          halign: "center",
          lineColor: [50, 50, 50],
          lineWidth: 0.3,
          fillColor: false,
        },
        columnStyles: {
          0: { halign: "left" },
          1: { halign: "center" },
        },
        theme: "grid",
        tableWidth: rightMargin - leftMargin,
        margin: { left: leftMargin },
        didDrawPage: (data) => {
          currentY = data.cursor.y;
        },
      });
    }
    autoTable(doc, {
      startY: currentY,
      head: [["Traits: - Social & Emotional", "Observation"]],
      body: traits.map((t) => [
        t.name || "N/A",
        (studentData.traitRemarks?.[`${studentId}-${t.id}`] || "-")
          .toLowerCase()
          .replace(/(^|\s)\w/g, (letter) => letter.toUpperCase()),
      ]),
      styles: {
        fontSize: 10,
        textColor: 0,
        halign: isPrepClass ? "left" : "center",
        lineColor: [50, 50, 50],
        lineWidth: 0.2,
      },
      headStyles: {
        textColor: 0,
        fontStyle: "bold",
        halign: "center",
        lineColor: [50, 50, 50],
        lineWidth: 0.3,
        fillColor: false,
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: isPrepClass ? "left" : "center" },
      },
      theme: "grid",
      tableWidth: rightMargin - leftMargin,
      margin: { left: leftMargin },
      didDrawPage: (data) => {
        currentY = data.cursor.y;
      },
    });
  }

  if (!isPrepClass && (isClass678 || isClass1to5_9_10)) {
    if (isClass678) {
      const tableWidth = (rightMargin - leftMargin) / 3 - 5;
      const tableLeftMargin = leftMargin;
      const rightTableStartY = currentY + 5;
      if (!totals.totalScored || !totals.totalMax || !totals.percentage || !totals.grade) {
        console.warn(`[Class ${selectedClass}] Incomplete totals data for student ${studentId}:`, totals);
      } else {
        autoTable(doc, {
          startY: rightTableStartY,
          head: [["Grand Total"]],
          body: [[`${totals.totalScored} / ${totals.totalMax}`]],
          styles: {
            fontSize: 8,
            fontStyle: "bold",
            halign: "center",
            textColor: 0,
            lineColor: [0, 0, 0],
            lineWidth: 0.2,
            minCellHeight: 6,
          },
          headStyles: {
            fillColor: false,
            fontStyle: "bold",
            textColor: 0,
            lineColor: [0, 0, 0],
            lineWidth: 0.4,
          },
          margin: { left: tableLeftMargin },
          tableWidth: tableWidth,
          theme: "grid",
        });
        autoTable(doc, {
          startY: rightTableStartY,
          head: [["Percentage"]],
          body: [[`${totals.percentage.toFixed(2)}%`]],
          styles: {
            fontSize: 8,
            fontStyle: "bold",
            halign: "center",
            textColor: 0,
            lineColor: [0, 0, 0],
            lineWidth: 0.2,
            minCellHeight: 6,
          },
          headStyles: {
            fillColor: false,
            fontStyle: "bold",
            textColor: 0,
            lineColor: [0, 0, 0],
            lineWidth: 0.4,
          },
          margin: { left: tableLeftMargin + tableWidth + 5 },
          tableWidth: tableWidth,
          theme: "grid",
        });
        autoTable(doc, {
          startY: rightTableStartY,
          head: [["Grade"]],
          body: [[totals.grade]],
          styles: {
            fontSize: 8,
            fontStyle: "bold",
            halign: "center",
            textColor: 0,
            lineColor: [0, 0, 0],
            lineWidth: 0.2,
            minCellHeight: 6,
          },
          headStyles: {
            fillColor: false,
            fontStyle: "bold",
            textColor: 0,
            lineColor: [0, 0, 0],
            lineWidth: 0.4,
          },
          margin: { left: tableLeftMargin + 2 * (tableWidth + 5) },
          tableWidth: tableWidth,
          theme: "grid",
          didDrawPage: (data) => {
            currentY = data.cursor.y + 5;
          },
        });
      }
    } else {
      const tableWidth = 40;
      const tableLeftMargin = 120;
      let verticalOffset = 20;
      if (["4", "5"].includes(selectedClass.trim())) {
        verticalOffset = 30;
      }
      const rightTableStartY = currentY - traits.length * 10 + verticalOffset + 15;
      autoTable(doc, {
        startY: rightTableStartY,
        head: [["Grand Total"]],
        body: [[`${totals.totalScored} / ${totals.totalMax}`]],
        styles: {
          fontSize: 10,
          fontStyle: "bold",
          halign: "center",
          textColor: 0,
          lineColor: [0, 0, 0],
          lineWidth: 0.2,
        },
        headStyles: {
          fillColor: false,
          fontStyle: "bold",
          textColor: 0,
          lineColor: [0, 0, 0],
          lineWidth: 0.4,
        },
        margin: { left: tableLeftMargin },
        tableWidth,
        theme: "grid",
      });
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 3,
        head: [["Percentage"]],
        body: [[`${totals.percentage.toFixed(2)}%`]],
        styles: {
          fontSize: 10,
          fontStyle: "bold",
          halign: "center",
          textColor: 0,
          lineColor: [0, 0, 0],
          lineWidth: 0.2,
        },
        headStyles: {
          fillColor: false,
          fontStyle: "bold",
          textColor: 0,
          lineColor: [0, 0, 0],
          lineWidth: 0.4,
        },
        margin: { left: tableLeftMargin },
        tableWidth,
        theme: "grid",
      });
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 3,
        head: [["Grade"]],
        body: [[totals.grade]],
        styles: {
          fontSize: 10,
          fontStyle: "bold",
          halign: "center",
          textColor: 0,
          lineColor: [0, 0, 0],
          lineWidth: 0.2,
        },
        headStyles: {
          fillColor: false,
          fontStyle: "bold",
          textColor: 0,
          lineColor: [0, 0, 0],
          lineWidth: 0.4,
        },
        margin: { left: tableLeftMargin },
        tableWidth,
        theme: "grid",
        didDrawPage: (data) => {
          currentY = data.cursor.y + 2;
        },
      });
    }
  }
// ────────────────────────────────────────────────
// REMARKS BOX + SIGNATURES (FINAL - 2cm DOWN for 1–5)
// ────────────────────────────────────────────────

const studentRemarks = (studentData.remarks?.[studentId] || "No remarks provided")
  .toLowerCase()
  .split(/(?<=[.?!])\s+/)
  .map(s => s.trim().replace(/\.$/, ''))
  .filter(s => s.length > 0)
  .map(s => s.charAt(0).toUpperCase() + s.slice(1))
  .join('. ')
  .replace(/\s+/g, ' ')
  .trim() + '.';

const lowerClass = selectedClass.trim().toLowerCase();
const classNum = parseInt(selectedClass.trim(), 10);

// ── Space BEFORE remarks box
let spaceBeforeRemarks = 8;

if ([1,2,3,4,5].includes(classNum)) {
  spaceBeforeRemarks = 12;
} else if ([6,7,8].includes(classNum)) {
  spaceBeforeRemarks = 4;
}

currentY += spaceBeforeRemarks;

// ── Remarks box
doc.setFontSize(11);
doc.setFont("helvetica", "normal");

const boxPadding = 6;
const boxWidth = (rightMargin - leftMargin) - boxPadding * 2;
const boxX = leftMargin + boxPadding;

const centerX = boxX + boxWidth / 2;
const lineSpacing = 5.0;
const maxTextWidth = boxWidth - 12;

const splitRemarks = doc.splitTextToSize(studentRemarks, maxTextWidth);

let boxHeight = splitRemarks.length * lineSpacing + 12;
boxHeight = Math.max(boxHeight, 20);

doc.setDrawColor(0);
doc.setLineWidth(0.25);

// draw box
doc.rect(boxX, currentY - 1, boxWidth, boxHeight);

// ── Text inside box
let topPadding = 5;

if ([1,2,3,4,5].includes(classNum)) {
  topPadding = 7;
}

splitRemarks.forEach((line, i) => {
  doc.text(line, centerX, currentY + topPadding + i * lineSpacing, { align: "center" });
});

// ── Space AFTER box
let spaceAfterRemarks = 10;

if ([1,2,3,4,5].includes(classNum)) {
  spaceAfterRemarks = 10;   // keep normal (don’t over push)
} else if ([6,7,8].includes(classNum)) {
  spaceAfterRemarks = 8;    // PREP untouched
}

// 🔥 2 CM DOWN ONLY FOR 1–5
let signatureAdjust = 0;

if ([1,2,3,4,5].includes(classNum)) {
  signatureAdjust = 9;   // 👈 ~2 cm down
}

currentY += boxHeight + spaceAfterRemarks + signatureAdjust;

// ── Signatures
doc.setFontSize(12);
doc.setFont("helvetica", "normal");

let middleAuthority = "Principal";
if (["prep i", "prep ii", "prep iii"].includes(lowerClass)) {
  middleAuthority = "KG Head";
} else if ([1,2,3,4,5].includes(classNum)) {
  middleAuthority = "Headmistress";
}

doc.text("Class Teacher", leftMargin + 5, currentY);
doc.text(middleAuthority, 105, currentY, { align: "center" });
doc.text("Parent/Guardian", rightMargin - 5, currentY, { align: "right" });

// ── Save or next page
if (isSinglePDF) {
  const fileName = `${studentName}_${studentId}_Report.pdf`;
  doc.save(fileName);
} else if (!isLastStudent) {
  doc.addPage();
}
return doc;
};

const downloadAllPDFs = async () => {
  if (students.length === 0) {
    toast.error("No students available to generate PDF.");
    return;
  }

  if (Object.keys(allStudentsData).length === 0 || Object.keys(allStudentsData).length !== students.length) {
    toast.warn("Fetching marks for all students, please wait...");
    await fetchMarksForAllStudents();
    if (Object.keys(allStudentsData).length !== students.length) {
      toast.error("Incomplete student data. Cannot generate PDFs.");
      return;
    }
  }

  console.log(`[Class ${selectedClass}] Starting PDF generation for ${students.length} students. allStudentsData:`, allStudentsData);
  let doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  for (let i = 0; i < students.length; i++) {
    const student = students[i];
    console.log(`[Class ${selectedClass}] Generating PDF for student ${i + 1}: ${student.student_id}, Name: ${student.student_name}, Data:`, allStudentsData[student.student_id]);
    const isLastStudent = i === students.length - 1;
    doc = generatePDF(student, doc, isLastStudent);
  }

  const fileName = `Class_${selectedClass}_${selectedSection}_Report.pdf`;
  doc.save(fileName);
  toast.success("Downloaded combined PDF for all students.");
};

const createPDF = (student, doc = null, isLastStudent = true) => {
  const isSinglePDF = !doc;
  if (isSinglePDF) {
    doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  }
  const leftMargin = 20;
  const rightMargin = 210 - leftMargin;

  doc.addImage(logo, "PNG", 14, 8, 25, 25);

  // Add "ACADEMIC PROFILE" badge at top-right corner (no padding)
  const profileText = "ACADEMIC PROFILE";
  const fontSize = 8;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(fontSize);
  const textWidth = doc.getTextWidth(profileText);
  const textHeight = fontSize; // Approx height in mm
  const badgeBoxWidth = textWidth;
  const badgeBoxHeight = textHeight;
  const badgeX = 210 - badgeBoxWidth - 10; // 10mm margin from right
  const badgeY = 10; // top margin
  doc.setFillColor(255, 102, 0); // Pink background
  doc.setTextColor(255, 255, 255); // White text
  doc.roundedRect(badgeX, badgeY, badgeBoxWidth, badgeBoxHeight, 2, 2, "F");
  // Center text in badge
  doc.text(profileText, badgeX + badgeBoxWidth / 2, badgeY + badgeBoxHeight / 2 + 1, {
    align: "center",
    baseline: "middle",
  });
  doc.setTextColor(0, 0, 0); // Reset to black

  const studentId = student.student_id;
  const studentName = student.student_name;
  const studentData = allStudentsData[studentId] || {};
  const studentStream = studentData.stream?.toLowerCase() || "science";
  console.log(`[Class ${selectedClass}] Generating PDF for ${studentId} (${studentName}). Stream: ${studentStream}`);

  const scholasticSubjects = getFilteredSubjects(studentStream);
  const coScholasticSubjects = classSubjects[selectedClass]?.coScholastic || [];
  const traits = classSubjects[selectedClass]?.traitObservations || [];
  const testType = selectedTest.toLowerCase().includes("unit") ? "unitTest" : "termExam";
  console.log(`Selected Test: ${selectedTest}, Determined testType: ${testType}`);

  if (Object.keys(studentData).length === 0) {
    console.warn(`[Class ${selectedClass}] No data found for student ${studentId}`);
  }

  const totals = calculateTotals(studentId);

  const isPrepClass = ["prep i", "prep ii", "prep iii"].includes(selectedClass.toLowerCase().trim());
  const isClass678 = ["6", "7", "8"].includes(selectedClass.toLowerCase().trim());
  const isClass1to11 = /^[1-9]$|10|11/.test(selectedClass.trim());
  const isClass1to5 = ["1", "2", "3", "4", "5"].includes(selectedClass.toLowerCase().trim());
  const isClass1to5_9_10 = ["1", "2", "3", "4", "5", "9", "10"].includes(selectedClass.trim());

  let currentY = isSinglePDF || doc.getNumberOfPages() === 1 ? 32 : 32;

  doc.setFontSize(14);
  doc.setFont(undefined, "bold");

  // Dynamic test heading
  const getTestDisplayName = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const nextYear = currentYear + 1;
    const testYear = `${currentYear}–${String(nextYear).slice(-2)}`;
    if (isPrepClass) {
      switch (selectedTest) {
        case "I Unit Test":
          return `I Unit Assessment (${testYear})`;
        case "II Unit Test":
          return `II Unit Assessment (${testYear})`;
        case "I Term Examination":
          return `I Term Assessment (${testYear})`;
        case "Annual Examination":
          return `Annual Assessment (${testYear})`;
        default:
          return `${selectedTest} (${testYear})`;
      }
    } else {
      return `${selectedTest} (${testYear})`;
    }
  };

  const heading = getTestDisplayName();
  doc.text(heading, 105, currentY, { align: "center" });
  currentY += 5;

  doc.setFontSize(12);
  doc.setFont(undefined, "normal");
  doc.setFont(undefined, "bold");
  doc.text(`Name: ${studentName}`, leftMargin, currentY);
  doc.setFont(undefined, "normal");
  doc.text(`Class: ${selectedClass} - ${selectedSection}`, rightMargin, currentY, { align: "right" });
  currentY += 5;

  doc.setFont(undefined, "bold");
  doc.text("Scholastic", leftMargin, currentY);
  currentY += 2;
  doc.setFont(undefined, "normal");

  // Correct grade logic - Final version
// 94 and above = A+, 84 to 93.99 = A
const getGrade = (obtained, max) => {
  if (!obtained || obtained === "" || obtained === "-" || max === 0) {
    return "";
  }

  const numObtained = parseFloat(obtained);
  if (isNaN(numObtained) || numObtained > max) {
    return obtained;   // Return as-is if teacher entered grade directly
  }

  const percentage = (numObtained / max) * 100;

  return percentage >= 94 ? "A+" :
         percentage >= 84 ? "A" :
         percentage >= 69 ? "B+" :
         percentage >= 49 ? "B" :
         percentage >= 39 ? "C" : "D";
};
  const scholasticData = [];

  if (isPrepClass) {
    scholasticSubjects.forEach((subject) => {
      const subjectId = subject.id;
      const subjectName = subject.name || "N/A";
      const subjectSpecs = subject.specs || [];
      if (subjectSpecs.length > 0) {
        subjectSpecs.forEach((spec, index) => {
          const key = `${studentId}-${subjectId}-${spec.id}-obtained`;
          const obtained = studentData.marks?.[key] ?? "-";
          scholasticData.push([
            index === 0 ? subjectName : "",
            spec.name || spec.id || "Spec",
            obtained === "0" ? "" : obtained,
          ]);
        });
      } else {
        const key = `${studentId}-${subjectId}-obtained`;
        const obtained = studentData.marks?.[key] ?? "-";
        scholasticData.push([
          subjectName,
          subject.spec || "",
          obtained === "0" ? "" : obtained,
        ]);
      }
    });
  } else if (["6", "7", "8", "9", "10"].includes(selectedClass.trim())) {
  scholasticSubjects.forEach((subject) => {
    const subjectId = subject.id;
    const subjectName = subject.name || "N/A";

    const obtainedKey = `${studentId}-${subjectId}-obtained`;
    const obtained = studentData.marks?.[obtainedKey] ?? "-";

    // ==================== MAX MARKS LOGIC ====================
    let max = 100;

    if (["6", "7", "8"].includes(selectedClass.trim())) {
      const lowerId = (subjectId || "").toString().toLowerCase().trim();
      const lowerName = (subjectName || "").toString().toLowerCase().trim();

      const isThirdLanguage =
        lowerId.includes("iii") ||
        lowerId.includes("3rd") ||
        lowerName.includes("iii language") ||
        lowerName.includes("3rd language") ||
        lowerName.includes("third language") ||
        lowerName.includes("iii lang") ||
        lowerName === "iii" ||
        lowerName === "third lang";

      if (testType === "unitTest") {
        max = isThirdLanguage ? 25 : 50;
      } else {
        // Annual Examination - III Language = 50, Others = 100
        max = isThirdLanguage ? 50 : 100;
      }
    } else {
      // Class 9 & 10
      const maxFromSubject = subject.maxMarks?.[testType] || (testType === "unitTest" ? 50 : 100);
      const maxKey = `${studentId}-${subjectId}`;
      const maxFromStudent = parseFloat(studentData.maxMarks?.[maxKey]);
      max = (!isNaN(maxFromStudent) && maxFromStudent > 0)
        ? maxFromStudent
        : (maxFromSubject || 100);
    }

    if (!max || isNaN(max) || max <= 0) max = 100;

    // ==================== GRADE CALCULATION ====================
    let grade = "";
    if (obtained !== "-" && obtained !== "" && !isNaN(parseFloat(obtained))) {
      grade = getGrade(obtained, max);   // Uses your getGrade (88+ = A+)
    } else {
      grade = obtained;   // If grade was entered directly
    }

    scholasticData.push([
      subjectName,
      obtained === "0" ? "" : max.toString(),   // Max Marks
      obtained === "0" ? "" : obtained,         // Marks Obtained
      grade                                     // Grade
    ]);

    console.log(`[PDF ${selectedClass}] ${subjectName} → Max=${max}, Obtained=${obtained}, Grade=${grade}`);
  });
}else {
    const groupedSubjects = scholasticSubjects.reduce((acc, subject) => {
      const subjectName = subject.name || "Unnamed";
      const subjectId = subject.id;
      if (!acc[subjectName]) {
        acc[subjectName] = { specs: [], totalMax: 0, totalObtained: 0, hasMissing: false };
      }
      const subjectSpecs = subject.specs?.length
        ? subject.specs
        : [{ id: subjectId, name: subject.spec, maxMarks: subject.maxMarks?.[testType] || 100 }];
      subjectSpecs.forEach((spec) => {
        const rawSpecId = spec.id || subjectId;
        const key = `${studentId}-${rawSpecId}-obtained`;
        const maxKey = `${studentId}-${rawSpecId}`;
        const obtainedRaw = studentData.marks?.[key];
        const obtained = obtainedRaw ?? "-";
        const max = parseFloat(studentData.maxMarks?.[maxKey]) || spec.maxMarks || subject.maxMarks?.[testType] || 100;
        acc[subjectName].specs.push({
          specName: spec.name || rawSpecId,
          obtained,
          max,
        });
        acc[subjectName].totalMax += max;
        if (obtained !== "-") {
          acc[subjectName].totalObtained += parseFloat(obtained) || 0;
        } else {
          acc[subjectName].hasMissing = true;
        }
      });
      return acc;
    }, {});
    Object.entries(groupedSubjects).forEach(([subjectName, { specs, totalMax, totalObtained, hasMissing }]) => {
      const totalGrade = hasMissing ? "" : getGrade(totalObtained, totalMax);
      specs.forEach((spec, index) => {
        const isFirst = index === 0;
        console.log(`[1-5] Subject: ${subjectName}, Spec: ${spec.specName}, Max: ${spec.max}, Obtained: ${spec.obtained}, Total: ${isFirst ? (hasMissing ? "" : totalObtained) : ""}, Grade: ${isFirst ? totalGrade : ""}`);
        scholasticData.push([
          isFirst ? subjectName : "",
          spec.specName,
          spec.obtained === "0" ? "" : spec.max.toString(),
          spec.obtained === "0" ? "" : spec.obtained,
          isFirst ? (hasMissing ? "" : totalObtained.toString()) : "",
          isFirst ? totalGrade : "",
        ]);
      });
    });
  }

  autoTable(doc, {
  startY: currentY,
  head: isPrepClass
    ? [["Subjects", "Assessment Specification", "Grade"]]
    : ["6", "7", "8", "9", "10"].includes(selectedClass.trim())
    ? [["Subjects", "Max Marks", "Marks Obtained", "Grade"]]
    : [["Subjects", "Assessment Specification", "Max Marks", "Marks Obtained", "Total Marks", "Grade"]],
  body: scholasticData,
  styles: {
    fontSize: isPrepClass ? 10.5 : 10,   // ⬅️ Just a touch bigger than default
    textColor: 0,
    valign: "middle",
    halign: "center",
    lineColor: [50, 50, 50],
    lineWidth: 0.2,
    ...(isPrepClass && {
      minCellHeight: 7,                  // ⬅️ Slightly taller
      cellPadding: { top: 1, right: 2, bottom: 1, left: 2 }, // ⬅️ Slight bump
    }),
    ...(["6", "7", "8", "9", "10"].includes(selectedClass.trim()) && {
      minCellHeight: 8,
      cellPadding: { top: 1, right: 2, bottom: 1, left: 2 },
    }),
    ...(!isPrepClass && !["6", "7", "8", "9", "10"].includes(selectedClass.trim()) && {
      minCellHeight: 5,
      cellPadding: { top: 0.5, right: 1.5, bottom: 0.5, left: 1.5 },
    }),
  },
  headStyles: {
    textColor: 0,
    fontStyle: "bold",
    halign: "center",
    valign: "middle",
    lineColor: [50, 50, 50],
    lineWidth: 0.3,
    fillColor: false,
    fontSize: isPrepClass ? 10.8 : 10,   // ⬅️ Subtle header increase
  },
  columnStyles: {
    ...(isPrepClass && {
      0: { halign: "center", cellWidth: 50 },
      1: { halign: "left", cellWidth: 90 },
      2: { fontStyle: "bold", halign: "center", cellWidth: 30 },
    }),
    ...(["6", "7", "8", "9", "10"].includes(selectedClass.trim()) && {
      0: { halign: "center" },
      1: { fontStyle: "bold", halign: "center" },
      2: { fontStyle: "bold", halign: "center" },
      3: { fontStyle: "bold", halign: "center" },
    }),
    ...(!isPrepClass && !["6", "7", "8", "9", "10"].includes(selectedClass.trim()) && {
      0: { cellWidth: 33, halign: "center", valign: "middle" },
      1: { cellWidth: 60 },
      2: { cellWidth: 17, fontStyle: "bold", halign: "center" },
      3: { cellWidth: 22, fontStyle: "bold", halign: "center" },
      4: { cellWidth: 17, fontStyle: "bold", halign: "center" },
      5: { cellWidth: 14, fontStyle: "bold", halign: "center" },
    }),
  },
  theme: "grid",
  tableWidth: rightMargin - leftMargin,
  margin: { left: leftMargin },
  didDrawPage: (data) => {
    currentY = data.cursor.y + 3;
  },
  didParseCell: function (data) {
    const isOldFormat = !["6", "7", "8", "9", "10"].includes(selectedClass.trim());
    if (data.section === "body" && isOldFormat) {
      const rowData = scholasticData[data.row.index];
      if (rowData) {
        if (data.column.index === 0 && rowData[0] === "") {
          data.cell.styles.lineWidth = {
            top: 0,
            right: 0.2,
            bottom: 0.2,
            left: 0.2,
          };
        } else {
          data.cell.styles.valign = "middle";
        }
        if (data.column.index === 4 && rowData[4] === "") {
          data.cell.styles.lineWidth = {
            top: 0,
            right: 0.2,
            bottom: 0.2,
            left: 0.2,
          };
        }
        if (data.column.index === 5 && rowData[5] === "") {
          data.cell.styles.lineWidth = {
            top: 0,
            right: 0.2,
            bottom: 0.2,
            left: 0.2,
          };
        }
      }
    }
  },
});

  if (isClass678) {
    const coX = leftMargin;
    const traitX = 110;
    const coBody = coScholasticSubjects.map((c) => [
      c.name || "N/A",
      studentData.coMarks?.[`${studentId}-${c.id}-obtained`] || "-",
    ]);
    const traitBody = traits.map((t) => [
      t.name || "N/A",
      studentData.traitRemarks?.[`${studentId}-${t.id}`] || "-",
    ]);
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    if (selectedClass.trim().toLowerCase() === "6","7","8") {
      currentY += 5;
    }
    doc.text("Co-Scholastic", coX, currentY);
    doc.text("Observations", traitX, currentY);
    currentY += 5;
    
    autoTable(doc, {
      startY: currentY,
      head: [["Subjects", "Grade"]],
      body: coBody,
      styles: {
        fontSize: 10,
        textColor: 0,
        halign: "center",
        lineColor: [50, 50, 50],
        lineWidth: 0.2,
      },
      headStyles: {
        textColor: 0,
        fontStyle: "bold",
        halign: "center",
        lineColor: [50, 50, 50],
        lineWidth: 0.3,
        fillColor: false,
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "center", fontStyle: "bold" },
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 0) {
          data.cell.styles.fontStyle = "normal";
        }
      },
      theme: "grid",
      tableWidth: 80,
      margin: { left: coX },
      pageBreak: "avoid",
    });
    autoTable(doc, {
      startY: currentY,
      head: [["Subjects", "Grade"]],
      body: traitBody,
      styles: {
        fontSize: 10,
        textColor: 0,
        halign: "center",
        lineColor: [50, 50, 50],
        lineWidth: 0.2,
      },
      headStyles: {
        textColor: 0,
        fontStyle: "bold",
        halign: "center",
        lineColor: [50, 50, 50],
        lineWidth: 0.3,
        fillColor: false,
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "center", fontStyle: "bold" },
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 0) {
          data.cell.styles.fontStyle = "normal";
        }
      },
      theme: "grid",
      tableWidth: 80,
      margin: { left: traitX },
      pageBreak: "avoid",
      didDrawPage: (data) => {
        currentY = data.cursor.y + 3;
      },
    });
  } else if (["1", "2", "3", "4", "5", "9", "10"].includes(selectedClass.trim())) {
    const traitX = leftMargin;
    const allTraits = traits.map((t) => ({
      name: t.name || "N/A",
      value: studentData.traitRemarks?.[`${studentId}-${t.id}`] || "-",
    }));
    const mainTraits = allTraits.filter((t) => t.name.toLowerCase() !== "supw");
    const supwTrait = allTraits.find((t) => t.name.toLowerCase() === "supw");
    const traitBody = mainTraits.map((t) => [t.name, t.value]);
    const supwBody = supwTrait ? [[supwTrait.name, supwTrait.value]] : [];
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text("Observations", traitX, currentY + 5);
    currentY += 10;
    let minCellHeight = null;
    let cellPadding = null;
    switch (selectedClass.trim()) {
      case "3":
        minCellHeight = 7;
        cellPadding = { top: 1, bottom: 1, left: 2, right: 2 };
        break;
      case "4":
        minCellHeight = 6;
        cellPadding = { top: 1, bottom: 1, left: 1.5, right: 1.5 };
        break;
      case "5":
        minCellHeight = 6;
        cellPadding = { top: 0.5, bottom: 0.5, left: 1, right: 1 };
        break;
      case "9":
      case "10":
        minCellHeight = 8;
        cellPadding = { top: 2, bottom: 2, left: 2, right: 2 };
        break;
      default:
        minCellHeight = 8;
        cellPadding = { top: 1, bottom: 1, left: 2, right: 2 };
        break;
    }
    autoTable(doc, {
      startY: currentY,
      head: [["Subjects", "Grade"]],
      body: traitBody,
      styles: {
        fontSize: 10,
        textColor: 0,
        halign: "center",
        lineColor: [50, 50, 50],
        lineWidth: 0.2,
        ...(minCellHeight && { minCellHeight }),
        ...(cellPadding && { cellPadding }),
      },
      headStyles: {
        textColor: 0,
        fontStyle: "bold",
        halign: "center",
        lineColor: [50, 50, 50],
        lineWidth: 0.3,
        fillColor: false,
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "center", fontStyle: "bold" },
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 0) {
          data.cell.styles.fontStyle = "normal";
        }
      },
      theme: "grid",
      tableWidth: 80,
      margin: { left: traitX },
      pageBreak: "avoid",
      didDrawPage: (data) => {
        currentY = data.cursor.y + 2;
      },
    });
    if (supwBody.length > 0) {
      currentY += 2;
      autoTable(doc, {
        startY: currentY,
        head: [["Subjects", "Grade"]],
        body: supwBody,
        showHead: "never",
        styles: {
          fontSize: 10,
          textColor: 0,
          halign: "center",
          lineColor: [50, 50, 50],
          lineWidth: 0.2,
          ...(minCellHeight && { minCellHeight }),
          ...(cellPadding && { cellPadding }),
        },
        headStyles: {
          textColor: 0,
          fontStyle: "bold",
          halign: "center",
          lineColor: [50, 50, 50],
          lineWidth: 0.3,
          fillColor: false,
        },
        columnStyles: {
          0: { halign: "left", cellWidth: 60 },
          1: { halign: "center", fontStyle: "bold", cellWidth: 20 },
        },
        didParseCell: (data) => {
          if (data.section === "body" && data.column.index === 0) {
            data.cell.styles.fontStyle = "normal";
          }
        },
        theme: "grid",
        tableWidth: 80,
        margin: { left: traitX },
        pageBreak: "avoid",
        didDrawPage: (data) => {
          currentY = data.cursor.y + 2;
        },
      });
    }
  } else {
    if (!isPrepClass && coScholasticSubjects.length > 0) {
      autoTable(doc, {
        startY: currentY,
        head: [["Co-Scholastic", "Grade"]],
        body: coScholasticSubjects.map((c) => [
          c.name || "N/A",
          studentData.coMarks?.[`${studentId}-${c.id}-obtained`] || "-",
        ]),
        styles: {
          fontSize: 10,
          textColor: 0,
          lineColor: [50, 50, 50],
          lineWidth: 0.2,
        },
        headStyles: {
          textColor: 0,
          fontStyle: "bold",
          halign: "center",
          lineColor: [50, 50, 50],
          lineWidth: 0.3,
          fillColor: false,
        },
        columnStyles: {
          0: { halign: "left" },
          1: { halign: "center" },
        },
        theme: "grid",
        tableWidth: rightMargin - leftMargin,
        margin: { left: leftMargin },
        didDrawPage: (data) => {
          currentY = data.cursor.y;
        },
      });
    }
    autoTable(doc, {
      startY: currentY,
      head: [["Traits: - Social & Emotional", "Observation"]],
      body: traits.map((t) => [
        t.name || "N/A",
        (studentData.traitRemarks?.[`${studentId}-${t.id}`] || "-")
          .toLowerCase()
          .replace(/(^|\s)\w/g, (letter) => letter.toUpperCase()),
      ]),
      styles: {
        fontSize: 10,
        textColor: 0,
        halign: isPrepClass ? "left" : "center",
        lineColor: [50, 50, 50],
        lineWidth: 0.2,
      },
      headStyles: {
        textColor: 0,
        fontStyle: "bold",
        halign: "center",
        lineColor: [50, 50, 50],
        lineWidth: 0.3,
        fillColor: false,
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: isPrepClass ? "left" : "center" },
      },
      theme: "grid",
      tableWidth: rightMargin - leftMargin,
      margin: { left: leftMargin },
      didDrawPage: (data) => {
        currentY = data.cursor.y;
      },
    });
  }

  if (!isPrepClass && (isClass678 || isClass1to5_9_10)) {
    if (isClass678) {
      const tableWidth = (rightMargin - leftMargin) / 3 - 5;
      const tableLeftMargin = leftMargin;
      const rightTableStartY = currentY + 5;
      if (!totals.totalScored || !totals.totalMax || !totals.percentage || !totals.grade) {
        console.warn(`[Class ${selectedClass}] Incomplete totals data for student ${studentId}:`, totals);
      } else {
        autoTable(doc, {
          startY: rightTableStartY,
          head: [["Grand Total"]],
          body: [[`${totals.totalScored} / ${totals.totalMax}`]],
          styles: {
            fontSize: 8,
            fontStyle: "bold",
            halign: "center",
            textColor: 0,
            lineColor: [0, 0, 0],
            lineWidth: 0.2,
            minCellHeight: 6,
          },
          headStyles: {
            fillColor: false,
            fontStyle: "bold",
            textColor: 0,
            lineColor: [0, 0, 0],
            lineWidth: 0.4,
          },
          margin: { left: tableLeftMargin },
          tableWidth: tableWidth,
          theme: "grid",
        });
        autoTable(doc, {
          startY: rightTableStartY,
          head: [["Percentage"]],
          body: [[`${totals.percentage.toFixed(2)}%`]],
          styles: {
            fontSize: 8,
            fontStyle: "bold",
            halign: "center",
            textColor: 0,
            lineColor: [0, 0, 0],
            lineWidth: 0.2,
            minCellHeight: 6,
          },
          headStyles: {
            fillColor: false,
            fontStyle: "bold",
            textColor: 0,
            lineColor: [0, 0, 0],
            lineWidth: 0.4,
          },
          margin: { left: tableLeftMargin + tableWidth + 5 },
          tableWidth: tableWidth,
          theme: "grid",
        });
        autoTable(doc, {
          startY: rightTableStartY,
          head: [["Grade"]],
          body: [[totals.grade]],
          styles: {
            fontSize: 8,
            fontStyle: "bold",
            halign: "center",
            textColor: 0,
            lineColor: [0, 0, 0],
            lineWidth: 0.2,
            minCellHeight: 6,
          },
          headStyles: {
            fillColor: false,
            fontStyle: "bold",
            textColor: 0,
            lineColor: [0, 0, 0],
            lineWidth: 0.4,
          },
          margin: { left: tableLeftMargin + 2 * (tableWidth + 5) },
          tableWidth: tableWidth,
          theme: "grid",
          didDrawPage: (data) => {
            currentY = data.cursor.y + 5;
          },
        });
      }
    } else {
      const tableWidth = 40;
      const tableLeftMargin = 120;
      let verticalOffset = 20;
      if (["4", "5"].includes(selectedClass.trim())) {
        verticalOffset = 30;
      }
      const rightTableStartY = currentY - traits.length * 10 + verticalOffset + 15;
      autoTable(doc, {
        startY: rightTableStartY,
        head: [["Grand Total"]],
        body: [[`${totals.totalScored} / ${totals.totalMax}`]],
        styles: {
          fontSize: 10,
          fontStyle: "bold",
          halign: "center",
          textColor: 0,
          lineColor: [0, 0, 0],
          lineWidth: 0.2,
        },
        headStyles: {
          fillColor: false,
          fontStyle: "bold",
          textColor: 0,
          lineColor: [0, 0, 0],
          lineWidth: 0.4,
        },
        margin: { left: tableLeftMargin },
        tableWidth,
        theme: "grid",
      });
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 3,
        head: [["Percentage"]],
        body: [[`${totals.percentage.toFixed(2)}%`]],
        styles: {
          fontSize: 10,
          fontStyle: "bold",
          halign: "center",
          textColor: 0,
          lineColor: [0, 0, 0],
          lineWidth: 0.2,
        },
        headStyles: {
          fillColor: false,
          fontStyle: "bold",
          textColor: 0,
          lineColor: [0, 0, 0],
          lineWidth: 0.4,
        },
        margin: { left: tableLeftMargin },
        tableWidth,
        theme: "grid",
      });
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 3,
        head: [["Grade"]],
        body: [[totals.grade]],
        styles: {
          fontSize: 10,
          fontStyle: "bold",
          halign: "center",
          textColor: 0,
          lineColor: [0, 0, 0],
          lineWidth: 0.2,
        },
        headStyles: {
          fillColor: false,
          fontStyle: "bold",
          textColor: 0,
          lineColor: [0, 0, 0],
          lineWidth: 0.4,
        },
        margin: { left: tableLeftMargin },
        tableWidth,
        theme: "grid",
        didDrawPage: (data) => {
          currentY = data.cursor.y + 2;
        },
      });
    }
  }

 // ================= REMARKS TEXT FORMAT =================
const studentRemarks = (
  studentData.remarks?.[studentId] || "No remarks provided"
)
  .toLowerCase()
  .split(/(?<=[.?!])\s+/)
  .map(s => s.trim().replace(/\.$/, ""))
  .filter(s => s.length > 0)
  .map(s => s.charAt(0).toUpperCase() + s.slice(1))
  .join(". ")
  .replace(/\s+/g, " ")
  .trim() + ".";

// ================= CLASS NORMALIZE =================
const cls = selectedClass.trim().toLowerCase();

// ================= POSITION ADJUSTMENT =================
if (["prep iii", "1", "2", "3", "4", "5"].includes(cls)) {
  currentY += 14;
} else if (["6", "7", "8"].includes(cls)) {
  currentY += 8;
}

// ================= REMARKS BOX SETUP =================
const boxWidth = rightMargin - leftMargin;
const centerX = leftMargin + boxWidth / 2;

doc.setFont(undefined, "normal");
doc.setFontSize(11);

// Split text based on width
const splitRemarks = doc.splitTextToSize(
  studentRemarks,
  boxWidth - 14
);

// Dynamic height
const lineSpacing = 6;
const padding = 6;
const boxHeight =
  splitRemarks.length * lineSpacing + padding * 2;

// ================= DRAW BOX =================
doc.setDrawColor(0);
doc.setLineWidth(0.3);
doc.rect(leftMargin, currentY, boxWidth, boxHeight);

// ================= CENTER TEXT INSIDE BOX =================
let textStartY =
  currentY +
  boxHeight / 2 -
  (splitRemarks.length * lineSpacing) / 2 +
  lineSpacing / 2;

splitRemarks.forEach((line, i) => {
  doc.text(
    line,
    centerX,
    textStartY + i * lineSpacing,
    { align: "center" }
  );
});

// Move cursor below remarks box
currentY += boxHeight + 14;

// ================= SIGNATURE SECTION =================
doc.setFontSize(12);

const pageWidth = 210;
let middleAuthority = "Principal";

if (["prep i", "prep ii", "prep iii"].includes(cls)) {
  middleAuthority = "KG Head";
} else if (["1", "2", "3", "4", "5"].includes(cls)) {
  middleAuthority = "Headmistress";
}

// Fine tuning for middle classes
if (["6", "7", "8"].includes(cls)) {
  currentY -= 10;
}

currentY += 6;

doc.text("Class Teacher", leftMargin, currentY);
doc.text(middleAuthority, pageWidth / 2, currentY, { align: "center" });
doc.text("Parent/Guardian", rightMargin, currentY, { align: "right" });

// ================= PDF SAVE / PAGE BREAK =================
if (isSinglePDF) {
  const fileName = `${studentName}_${studentId}_Report.pdf`;
  doc.save(fileName);
} else if (!isLastStudent) {
  doc.addPage();
}

return doc;
};

const handlePublish = async () => {
  if (students.length === 0) {
    toast.error("No students available to publish PDFs.");
    return;
  }

  if (!canEdit()) {
    const headRole = getRequiredHead(selectedClass);
    toast.error(`Cannot publish PDFs. Marks are finalized. Contact the ${headRole}.`);
    return;
  }

  if (isPublishing) {
    toast.warn("Publishing in progress, please wait...");
    return;
  }

  if (
    Object.keys(allStudentsData).length === 0 ||
    Object.keys(allStudentsData).length !== students.length
  ) {
    toast.warn("Fetching marks for all students, please wait...");
    await fetchMarksForAllStudents();
    if (Object.keys(allStudentsData).length !== students.length) {
      return;
    }
  }

  setIsPublishing(true);
  let successCount = 0;

  try {
    for (const student of students) {
      if (!student.student_id || !student.student_name) {
        console.warn(`[Class ${selectedClass}] Skipping student with invalid ID or name:`, student);
        continue;
      }

      try {
        const sanitizedStudentName = student.student_name.replace(/[\/\\:*?"<>|]/g, "_");

        // ✅ Compress PDF
        const doc = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
          compress: true, // 👈 Enable compression
        });

        // ✅ Use standard font (reduces size)
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);

       
        // ✅ Generate compact PDF content
        createPDF(student, doc, true);

        // ✅ Reduce precision for size reduction
        const pdfBlob = doc.output("blob", { precision: 2 }); // 👈 Reduce precision
        const fileName = `${sanitizedStudentName}_${student.student_id}_Report.pdf`;

        const formData = new FormData();
        formData.append("student_id[]", student.student_id);
        formData.append("class[]", selectedClass);
        formData.append("section[]", selectedSection);
        formData.append("test_name[]", selectedTest);
        formData.append("pdf[]", new File([pdfBlob], fileName, { type: "application/pdf" }));

        console.log(`[Class ${selectedClass}] Uploading PDF for ${student.student_id}: ${fileName}`);

        const response = await axios.post(`${API_URL}/upload-report`, formData, {
          headers: {
            "X-User-Role": userRole || "admin",
            "Content-Type": "multipart/form-data",
            "Cache-Control": "no-store",
          },
        });

        if (response.data.status === "success") {
          successCount++;
          console.log(`[Class ${selectedClass}] Uploaded successfully for ${student.student_id}`);
        } else {
          console.warn(`[Class ${selectedClass}] Upload failed for ${student.student_id}: ${response.data.message}`);
        }
      } catch (pdfError) {
        console.error(`[Class ${selectedClass}] PDF generation/upload error for ${student.student_id}:`, pdfError);
        toast.warn(`PDF failed for ${student.student_name}. Skipping.`);
      }
    }

    toast.success(`Successfully published ${successCount} PDFs!`);
  } catch (error) {
    const headRole = getRequiredHead(selectedClass);
    console.error(`[Class ${selectedClass}] Publish error:`, error.response?.data || error.message);
    toast.error(
      error.response?.data?.message ||
        `Error publishing PDFs. Contact the ${headRole}.`
    );
  } finally {
    setIsPublishing(false);
  }
};


  return (
    <div className="result-report">
      <ToastContainer />
      {loading ? (
        <p>Loading...</p>
      ) : students.length > 0 && ["teacher", "head", "admin"].includes(userRole || "teacher") ? (
        <div className="student-report">
          <h2>{getTestDisplayName()}</h2>


          <div className="top-header-row">
            <button onClick={onBack} className="back-button">
              ← Back
            </button>
            {(selectedClass === "9" || selectedClass === "10") && (
              <div className="stream-selector" style={{ marginLeft: "20px" }}>
                <label htmlFor="stream-select">Stream: </label>
                <select
                  id="stream-select"
                  value={selectedStream}
                  onChange={handleStreamChange}
                  style={{ padding: "5px", fontSize: "14px" }}
                >
                  <option value="science">Science</option>
                  <option value="commerce">Commerce</option>
                </select>
              </div>
            )}
            <div className="navigation-arrows top-nav">
              <button
                onClick={() => setCurrentStudentIndex((i) => Math.max(i - 1, 0))}
                disabled={currentStudentIndex === 0}
              >
                ←
              </button>
              <span>
                {currentStudentIndex + 1} of {students.length}
              </span>
              <button
                onClick={() => setCurrentStudentIndex((i) => Math.min(i + 1, students.length - 1))}
                disabled={currentStudentIndex === students.length - 1}
              >
                →
              </button>
            </div>
          </div>
          <div className="student-header">
            <div className="student-info">
              <h3>Name: {students[currentStudentIndex]?.student_name || "N/A"}</h3>
              <h3>Student ID: {students[currentStudentIndex]?.student_id || "N/A"}</h3>
              <h3>
                Class: {(students[currentStudentIndex]?.class || "N/A") + " - " + (students[currentStudentIndex]?.section || "N/A")}
                {selectedClass === "9" ? ` (${selectedStream.charAt(0).toUpperCase() + selectedStream.slice(1)})` : ""}
              </h3>
            </div>
          </div>

          <div className="scholastic-section">
  {template.renderScholastic(
    getFilteredSubjects(),
    marks,
    handleMarksChange,
    students[currentStudentIndex],
    !canEdit()
  )}
</div>
          <div
            className="co-traits-container"
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "20px",
              alignItems: "flex-start",
              justifyContent: "space-between",
              marginTop: "20px",
            }}
          >
            <div className="traits-section" style={{ flex: 1, minWidth: 0 }}>
              {traitRemarksLoading ? (
                <p>Loading trait remarks...</p>
              ) : traitRemarksError ? (
                <p style={{ color: "red", fontWeight: "bold" }}>{traitRemarksError}</p>
              ) : (
                template.renderTraits(
                  traitObservations,
                  traitRemarks,
                  handleTraitRemarksChange,
                  students[currentStudentIndex],
                  !canEdit()
                )
              )}
            </div>

            {coSubjects.length > 0 && (
              <div className="co-scholastic-section" style={{ flex: 1, minWidth: 0 }}>
                {coMarksLoading ? (
                  <p>Loading co-scholastic marks...</p>
                ) : coMarksError ? (
                  <p style={{ color: "red", fontWeight: "bold" }}>{coMarksError}</p>
                ) : (
                  template.renderCoScholastic(
                    coSubjects,
                    coMarks,
                    handleCoMarksChange,
                    students[currentStudentIndex],
                    !canEdit()
                  )
                )}
              </div>
            )}
          </div>

          {!["prep 1", "prep 2", "prep 3", "prep i", "prep ii", "prep iii", "Mont Care"].includes(selectedClass.toLowerCase().trim()) && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "10px",
                marginTop: "15px",
                marginBottom: "15px",
                fontFamily: "Arial, sans-serif",
              }}
            >
              <div
                style={{
                  border: "1px solid #000",
                  borderRadius: "5px",
                  padding: "6px 8px",
                  textAlign: "center",
                  width: "18%",
                }}
              >
                <div style={{ fontWeight: "bold", fontSize: "12px" }}>Grand Total</div>
                <div style={{ marginTop: "2px", fontSize: "13px" }}>
                  {calculateTotals(students[currentStudentIndex]?.student_id).totalScored} / {calculateTotals(students[currentStudentIndex]?.student_id).totalMax}
                </div>
              </div>
              <div
                style={{
                  border: "1px solid #000",
                  borderRadius: "5px",
                  padding: "6px 8px",
                  textAlign: "center",
                  width: "18%",
                }}
              >
                <div style={{ fontWeight: "bold", fontSize: "12px" }}>Percentage</div>
                <div style={{ marginTop: "2px", fontSize: "13px" }}>
                  {calculateTotals(students[currentStudentIndex]?.student_id).percentage.toFixed(2)}%
                </div>
              </div>
              <div
                style={{
                  border: "1px solid #000",
                  borderRadius: "5px",
                  padding: "6px 8px",
                  textAlign: "center",
                  width: "18%",
                }}
              >
                <div style={{ fontWeight: "bold", fontSize: "12px" }}>Grade</div>
                <div style={{ marginTop: "2px", fontSize: "13px" }}>
                  {calculateTotals(students[currentStudentIndex]?.student_id).grade}
                </div>
              </div>
            </div>
          )}

          <div className="remarks-section">
            <h4>Remarks</h4>
            <textarea
              id="remarks"
              value={remarks[students[currentStudentIndex]?.student_id] || ""}
              onChange={(e) => handleRemarksChange(students[currentStudentIndex]?.student_id, e.target.value)}
              disabled={!canEdit()}
              readOnly={!canEdit()}
              style={{ backgroundColor: !canEdit() ? "#f0f0f0" : "white" }}
              placeholder="Enter remarks here"
            />
          </div>

          <div className="navigation-submit-row">
            <button
              className="submit-button"
              onClick={handleSubmit}
              disabled={isSubmitting || !canEdit()}
            >
              {isSubmitting ? "Submitting..." : "Save"}
            </button>
            <button
              className="submit-button"
              style={{ backgroundColor: "#0066cc", color: "white" }}
              onClick={() => generatePDF(students[currentStudentIndex])}
            >
              <FontAwesomeIcon icon={faDownload} /> Download PDF
            </button>
            <button
              className="submit-button"
              style={{ backgroundColor: "#00aa66", color: "white" }}
              onClick={downloadAllPDFs}
            >
              <FontAwesomeIcon icon={faDownload} /> Download All PDFs
            </button>
            <button
              className="submit-button"
              style={{ backgroundColor: '#cc6600', color: 'white' }}
              onClick={handlePublish}
              disabled={isPublishing || !canEdit()}
            >
              <FontAwesomeIcon icon={faUpload} /> {isPublishing ? "Publishing..." : "Publish"}
            </button>

            <div className="navigation-arrows">
              <button
                onClick={() => setCurrentStudentIndex((i) => Math.max(i - 1, 0))}
                disabled={currentStudentIndex === 0}
              >
                ←
              </button>
              <span>
                {currentStudentIndex + 1} of {students.length}
              </span>
              <button
                onClick={() => setCurrentStudentIndex((i) => Math.min(i + 1, students.length - 1))}
                disabled={currentStudentIndex === students.length - 1}
              >
                →
              </button>
            </div>
          </div>
        </div>
      ) : (
        <p>No students found or access denied</p>
      )}
    </div>
  );
};

export default ResultReport;




